import React, { useEffect, useRef, useState, useCallback } from "react";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { useAxiosInstance } from "../services/Interceptor";
import LoadingSpinner from "./LoadingSpinner";
import {
  SearchAthletesPagedAsync,
  getAthleteCompareData,
  getAthleteProfile,
} from "../services/AthleteService";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import CompareModalTrigger from "./athlete-compare/CompareModalTrigger";
import CompareAthleteSearchPanel from "./athlete-compare/CompareAthleteSearchPanel";
import ComparePlayersHeader from "./athlete-compare/ComparePlayersHeader";
import ComparePlayersTable from "./athlete-compare/ComparePlayersTable";
import AthleteProfilePreviewModal from "./athlete-compare/AthleteProfilePreviewModal";
import AthleteCombineHistoryModal from "./athlete-compare/AthleteCombineHistoryModal";
import { toast } from "react-toastify";

const PAGE_SIZE = 20;

export default function ComparePlayersModal() {
  const authUser = useAuthUser();
  const isHighSchoolCoach = authUser?.user?.role === "highschool";
  const [openCompareModal, setOpenCompareModal] = useState(false);
  const [activeTab, setActiveTab] = useState("trackit");
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [currentAthlete, setCurrentAthlete] = useState(null);
  const apiInstance = useAxiosInstance();
  const [isAthleteSelected, setIsAthleteSelected] = useState(false);
  const searchAthleteRef = useRef();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedQ = useDebouncedValue(searchTerm, 250);
  const r_athlete = useSelector((state) => state.athletes);
  const [userId, setUserId] = useState(0);
  const [athleteCompareData, setAthleteCompareData] = useState();
  const comparisonRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [athleteId, setAthleteId] = useState(0);
  const [openCombineHistoryModal, setOpenCombineHistoryModal] = useState(false);

  // Height state: dynamic height adjusted based on screen size (mobile vs desktop)
  const [adjustedHeight, setAdjustedHeight] = useState(
    window.innerHeight - 420,
  );

  const [showSimilarDropdown, setShowSimilarDropdown] = useState(false);
  const [hasAppliedSimilarFilters, setHasAppliedSimilarFilters] =
    useState(false);
  // similarFilters drives the actual fetch; similarFiltersDraft drives the
  // checkbox UI inside the dropdown. The draft only commits to similarFilters
  // when the user clicks Apply — preventing every checkbox toggle from
  // re-running the search.
  const [similarFilters, setSimilarFilters] = useState({
    size: false,
    position: false,
    athleticism: false,
  });
  const [similarFiltersDraft, setSimilarFiltersDraft] = useState({
    size: false,
    position: false,
    athleticism: false,
  });

  // Paged results state — replaces the old Redux/cached-list flow.
  const [displayedAthletes, setDisplayedAthletes] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const cacheRef = useRef(new Map());
  const observerTarget = useRef(null);

  const toggleSimilarFilter = (key) => {
    setHasAppliedSimilarFilters(false);
    setSimilarFiltersDraft((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // When the dropdown opens, sync the draft to whatever's currently applied so the
  // checkboxes reflect the live filter state. When it closes without Apply, the
  // draft is left as-is so reopening shows the user's last selections, but the
  // applied state — and therefore the result list — is unchanged.
  const openSimilarDropdown = () => {
    setSimilarFiltersDraft(similarFilters);
    setShowSimilarDropdown(true);
  };

  const buildSimilarParam = useCallback(
    () =>
      Object.entries(similarFilters)
        .filter(([, checked]) => checked)
        .map(([k]) => k)
        .join(","),
    [similarFilters],
  );

  const refAthleteId = r_athlete?.profile?.id;

  // Height update logic: adjust height dynamically on resize for mobile & desktop
  useEffect(() => {
    const updateHeight = () => {
      const currentHeight = window.innerHeight;
      const isMobile = window.innerWidth < 768;
      const offset = isMobile ? 295 : 420;
      setAdjustedHeight(currentHeight - offset);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const fetchPage = useCallback(
    async (q, page, append) => {
      const similar = buildSimilarParam();
      const key = `${q}|${similar}|${refAthleteId || ""}|${page}`;
      const cached = cacheRef.current.get(key);
      if (cached) {
        if (append) {
          setDisplayedAthletes((prev) => [...prev, ...(cached.items || [])]);
        } else {
          setDisplayedAthletes(cached.items || []);
        }
        setHasMore(!!cached.hasMore);
        setCurrentPage(page);
        return;
      }

      const setBusy = append ? setIsLoadingMore : setLoading;
      setBusy(true);
      try {
        const data = await SearchAthletesPagedAsync(apiInstance, {
          q,
          page,
          pageSize: PAGE_SIZE,
          similar: similar || undefined,
          athleteId: refAthleteId,
        });
        cacheRef.current.set(key, data);
        if (append) {
          setDisplayedAthletes((prev) => [...prev, ...(data.items || [])]);
        } else {
          setDisplayedAthletes(data.items || []);
        }
        setHasMore(!!data.hasMore);
        setCurrentPage(page);
      } catch (err) {
        toast.error(
          err.response?.data?.message ||
            err.message ||
            "Failed to search athletes",
          { position: "top-right" },
        );
      } finally {
        setBusy(false);
      }
    },
    [apiInstance, buildSimilarParam, refAthleteId],
  );

  // Drop cached page entries whenever the similarity filter set changes. Without this,
  // entries from previously-applied filter combos accumulate for the lifetime of the
  // component (not a correctness bug — cache key includes the similar param — but
  // unbounded memory growth in a long session).
  useEffect(() => {
    cacheRef.current.clear();
  }, [similarFilters]);

  // Fetch page 0 on mount and whenever the modal opens / the debounced query / the similarity
  // filter set changes. We also re-fetch when the reference athlete changes.
  useEffect(() => {
    if (!openCompareModal) return;
    if (!refAthleteId) {
      setDisplayedAthletes([]);
      setLoading(false);
      return;
    }
    fetchPage(debouncedQ, 0, false);
  }, [openCompareModal, debouncedQ, similarFilters, refAthleteId, fetchPage]);

  // Infinite scroll inside the compare modal results list.
  useEffect(() => {
    const target = observerTarget.current;
    if (!target) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoadingMore &&
          !loading
        ) {
          fetchPage(debouncedQ, currentPage + 1, true);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(target);
    return () => observer.unobserve(target);
  }, [hasMore, isLoadingMore, loading, currentPage, debouncedQ, fetchPage]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const athleteSelected = (selectedAth) => {
    setUserId(selectedAth.userId);
    setSelectedAthlete(selectedAth);
    setIsAthleteSelected(true);
  };

  useEffect(() => {
    async function fetchData() {
      if (userId !== 0) {
        try {
          const response = await getAthleteProfile(apiInstance, userId);
          setSelectedAthlete(response);

          const comparedResponse = await getAthleteCompareData(
            apiInstance,
            r_athlete.profile.id,
            userId,
          );
          if (comparedResponse) {
            setAthleteCompareData(comparedResponse);
          }
        } catch (error) {
          toast.error(
            error.response?.data?.message ||
              "Failed to load athlete comparison data",
            { position: "top-right" },
          );
        }
      }
    }
    fetchData();
  }, [apiInstance, r_athlete.profile.id, userId]);

  useEffect(() => {
    async function fetchData() {
      if (r_athlete.profile.id) {
        try {
          const currentAthleteRes = await getAthleteProfile(
            apiInstance,
            r_athlete.profile.id,
          );
          if (currentAthleteRes) {
            setCurrentAthlete(currentAthleteRes);
          }
        } catch (error) {
          toast.error(
            error.response?.data?.message ||
              "Failed to load current athlete profile data",
            { position: "top-right" },
          );
        }
      }
    }
    fetchData();
  }, [apiInstance, r_athlete]);

  // Helper function to get measurement rows data with isBest property
  const getMeasurementRowsData = () => {
    if (!athleteCompareData?.measurements?.measurementRows) return [];

    return athleteCompareData.measurements.measurementRows.map((row) => [
      row.label,
      row.athlete.data,
      row.compareTo.data,
      row.athlete.isBest ? "1" : row.compareTo.isBest ? "2" : "0",
      row.athlete.isBest && row.compareTo.isBest ? "both" : "single",
    ]);
  };

  // Helper function to get best results data with isBest property
  const getBestResultsData = () => {
    if (!athleteCompareData?.bestResults) return [];

    return athleteCompareData.bestResults.map((test) => {
      const athleteResult = test.athlete?.result;
      const compareToResult = test.compareTo?.result;

      let athleteValue = "-";
      let compareToValue = "-";
      let physicalTestName = "";

      if (athleteResult) {
        if (test.physicalTestName?.toLowerCase().includes("bench press")) {
          athleteValue = `${athleteResult.stageValue} × ${athleteResult.reps}`;
          physicalTestName = test.physicalTestName;
        } else if (
          test.physicalTestName?.toLowerCase().includes("40 yard dash")
        ) {
          physicalTestName =
            test.stage === "40" ? "40 Yard Dash" : `${test.stage} Yard Split`;
          athleteValue =
            activeTab === "trackit"
              ? athleteResult.stageValue?.toFixed(2) || "-"
              : athleteResult.stageValue?.toFixed(2) || "-";
        } else {
          athleteValue =
            activeTab === "trackit"
              ? athleteResult.stageValue?.toFixed(2) || "-"
              : athleteResult.stageValue?.toFixed(2) || "-";
          physicalTestName = test.physicalTestName;
        }
      }

      if (compareToResult) {
        if (test.physicalTestName?.toLowerCase().includes("bench press")) {
          compareToValue = `${compareToResult.stageValue} × ${compareToResult.reps}`;
        } else if (
          test.physicalTestName?.toLowerCase().includes("40 yard dash")
        ) {
          compareToValue =
            activeTab === "trackit"
              ? compareToResult.stageValue?.toFixed(2) || "-"
              : compareToResult.stageValue?.toFixed(2) || "-";
        } else {
          compareToValue =
            activeTab === "trackit"
              ? compareToResult.stageValue?.toFixed(2) || "-"
              : compareToResult.stageValue?.toFixed(2) || "-";
        }
      }

      return [
        physicalTestName,
        athleteValue,
        compareToValue,
        test.athlete?.isBest ? "1" : test.compareTo?.isBest ? "2" : "0",
        test.athlete?.isBest && test.compareTo?.isBest ? "both" : "single",
      ];
    });
  };

  // Helper function to get the main measurement score based on active tab
  const getMainMeasurementScore = () => {
    if (!athleteCompareData?.measurements?.measurementScoreRows) return null;

    const mainScoreRow =
      athleteCompareData.measurements.measurementScoreRows.find(
        (row) =>
          row.label ===
          (activeTab === "trackit"
            ? "MeasurementScore"
            : "PositionMeasurementScore"),
      );

    if (mainScoreRow) {
      return [
        `${activeTab === "trackit" ? "Trackit Measurement Score" : "Position Measurement Score"}`,
        Math.round(mainScoreRow?.athlete?.data),
        Math.round(mainScoreRow?.compareTo?.data),
        mainScoreRow.athlete.isBest
          ? "1"
          : mainScoreRow.compareTo.isBest
            ? "2"
            : "0",
        mainScoreRow.athlete.isBest && mainScoreRow.compareTo.isBest
          ? "both"
          : "single",
      ];
    }

    return null;
  };

  // Helper function to get the main athletic score based on active tab
  const getMainAthleticScore = () => {
    const athleteScore =
      activeTab === "trackit"
        ? currentAthlete?.overallAthleticScore?.toFixed(2)
        : currentAthlete?.overallPositionAthleticScore?.toFixed(2);

    const compareToScore =
      activeTab === "trackit"
        ? selectedAthlete?.overallAthleticScore?.toFixed(2)
        : selectedAthlete?.overallPositionAthleticScore?.toFixed(2);

    const isAthleteBest =
      parseFloat(athleteScore || 0) > parseFloat(compareToScore || 0);
    const isCompareToBest =
      parseFloat(compareToScore || 0) > parseFloat(athleteScore || 0);
    const isBothBest =
      parseFloat(athleteScore || 0) === parseFloat(compareToScore || 0);

    return [
      `${activeTab === "trackit" ? "Trackit Athletic Score" : "Position Athletic Score"}`,
      athleteScore || "0",
      compareToScore || "0",
      isAthleteBest ? "1" : isCompareToBest ? "2" : "0",
      isBothBest ? "both" : "single",
    ];
  };

  const handleViewClick = (id) => {
    setAthleteId(id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setAthleteId(0);
    setIsModalOpen(false);
  };

  const handleBackToSearch = () => {
    setSelectedAthlete(null);
    setAthleteCompareData(null);
    setUserId(0);
    setIsAthleteSelected(false);
    setActiveTab("trackit");
    setSearchTerm("");
    setDisplayedAthletes([]);
    setCurrentPage(0);
    setHasMore(false);
    if (searchAthleteRef.current) {
      searchAthleteRef.current.value = "";
    }
    fetchPage("", 0, false);
  };

  if (r_athlete.loading)
    return (
      <div className="my-10 lg:w-full xl:h-full">
        <LoadingSpinner />
      </div>
    );

  const comparisonRows = [
    getMainMeasurementScore(),
    ...getMeasurementRowsData(),
    getMainAthleticScore(),
    ...getBestResultsData(),
  ].filter(Boolean);

  return (
    <div className="flex items-center gap-2">
      <CompareModalTrigger
        onOpen={() => {
          setOpenCompareModal(true);
          setSearchTerm("");
          setSelectedAthlete(null);
          setAthleteCompareData(null);
          setUserId(0);
          setIsAthleteSelected(false);
          setShowSimilarDropdown(false);
          setSimilarFilters({
            size: false,
            position: false,
            athleticism: false,
          });
          setSimilarFiltersDraft({
            size: false,
            position: false,
            athleticism: false,
          });
          setHasAppliedSimilarFilters(false);
          cacheRef.current.clear();
          setDisplayedAthletes([]);
          setCurrentPage(0);
        }}
        onCompareCombineHistory={() => setOpenCombineHistoryModal(true)}
      />

      <AthleteCombineHistoryModal
        open={openCombineHistoryModal}
        onClose={() => setOpenCombineHistoryModal(false)}
        athleteId={r_athlete.profile?.id}
        currentAthlete={currentAthlete}
      />

      {/* Modal */}
      {openCompareModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white w-[95%] sm:w-[90%] md:w-full max-w-3xl rounded-md relative max-h-[90vh] flex flex-col">
              <button
                onClick={() => {
                  setOpenCompareModal(false);
                  setIsAthleteSelected(false);
                  setAthleteCompareData(null);
                }}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-base z-10 gap-4"
              >
                <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
              </button>
              {!isAthleteSelected && (
                <CompareAthleteSearchPanel
                  showSimilarDropdown={showSimilarDropdown}
                  setShowSimilarDropdown={setShowSimilarDropdown}
                  onOpenSimilarDropdown={openSimilarDropdown}
                  similarFilters={similarFiltersDraft}
                  toggleSimilarFilter={toggleSimilarFilter}
                  onApplySimilarFilters={() => {
                    setSimilarFilters(similarFiltersDraft);
                    setShowSimilarDropdown(false);
                    setHasAppliedSimilarFilters(true);
                  }}
                  handleSearch={handleSearch}
                  searchAthleteRef={searchAthleteRef}
                  loading={loading}
                  shouldSearchAfterLoad={false}
                  adjustedHeight={adjustedHeight}
                  visibleAthletes={displayedAthletes}
                  hasAppliedSimilarFilters={hasAppliedSimilarFilters}
                  observerTarget={observerTarget}
                  isLoadingMore={isLoadingMore}
                  hasMore={hasMore}
                  onSelectAthlete={(athlete) => {
                    athleteSelected(athlete);
                  }}
                />
              )}

              {isAthleteSelected && athleteCompareData && (
                <div name="compare-athelete">
                  <ComparePlayersHeader
                    onBack={handleBackToSearch}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    comparisonRef={comparisonRef}
                  />
                  <div
                    ref={comparisonRef}
                    className="px-3 sm:px-4 py-3 sm:py-3 flex-1 h-[660px] overflow-y-auto"
                  >
                    <ComparePlayersTable
                      athleteCompareData={athleteCompareData}
                      currentAthlete={currentAthlete}
                      selectedAthlete={selectedAthlete}
                      isHighSchoolCoach={isHighSchoolCoach}
                      comparisonRows={comparisonRows}
                      onViewAthlete={handleViewClick}
                    />
                  </div>
                </div>
              )}
              {isAthleteSelected && !athleteCompareData && (
                <div className="flex justify-center items-center p-8">
                  <LoadingSpinner />
                </div>
              )}
            </div>
          </div>
          <div>
            <AthleteProfilePreviewModal
              isOpen={isModalOpen}
              athleteId={athleteId}
              onClose={handleCloseModal}
            />
          </div>
        </>
      )}
    </div>
  );
}
